import { Controller } from "@hotwired/stimulus"

const CAMERA_UNAVAILABLE_STORAGE_KEY = "tyreBarcodeScannerCameraUnavailable"
const BARCODE_CONFIRMATION_WINDOW_MS = 750
const activeTyreBarcodeScannerController = { current: null }

export default class extends Controller {
  static targets = ["input", "submitButton", "startButton", "manualButton", "manualInputContainer", "scanOptionsContainer", "stopButton", "video", "status", "cancelButton", "orText"]
  static values = {
    cameraOptionRemoved: Boolean,
    newItemUrl: String,
    scanBarcodeStatus: String,
    scanUnavailableStatus: String,
  }

  connect() {
    this.scanAnimationFrame = null
    this.mediaStream = null
    this.videoTrack = null
    this.zxingReader = null
    this.fallbackToZxingInProgress = false
    this.cameraScanning = false
    this.pendingBarcodeValue = null
    this.pendingBarcodeCount = 0
    this.pendingBarcodeFirstSeenAt = null
    this.barcodeDetector = this.buildBarcodeDetector()
    this.cameraOptionRemovedValue = this.cameraOptionRemovedValue === true || this.cameraUnavailableStored()

    if (this.cameraOptionRemovedValue === true) {
      this.removeCameraOption(true)
    }
  }

  disconnect() {
    if (activeTyreBarcodeScannerController.current === this) {
      activeTyreBarcodeScannerController.current = null
    }

    this.stopCamera()
  }

  toggleSubmitButtonDisabled() {
    this.hideItemFoundAlert()
    this.submitButtonTarget.disabled = this.inputTarget.value.trim().length === 0
  }

  shouldAcceptScannedBarcode(value, options = {}) {
    const barcodeValue = this.normalizedValue(value)
    if (!barcodeValue) {
      this.resetPendingBarcode()
      return false
    }

    const requiredConfirmations = options.requiredConfirmations
    const useConfirmationWindow = options.useConfirmationWindow !== false

    const now = Date.now()
    const withinConfirmationWindow = this.pendingBarcodeFirstSeenAt &&
      now - this.pendingBarcodeFirstSeenAt <= BARCODE_CONFIRMATION_WINDOW_MS

    if (
      this.pendingBarcodeValue === barcodeValue &&
      (!useConfirmationWindow || withinConfirmationWindow)
    ) {
      this.pendingBarcodeCount += 1
    } else {
      this.pendingBarcodeValue = barcodeValue
      this.pendingBarcodeCount = 1
      this.pendingBarcodeFirstSeenAt = now
    }

    return this.pendingBarcodeCount >= requiredConfirmations
  }

  resetPendingBarcode() {
    this.pendingBarcodeValue = null
    this.pendingBarcodeCount = 0
    this.pendingBarcodeFirstSeenAt = null
  }

  normalizedValue(value) {
    return (value || "").trim().toUpperCase()
  }

  openAddNewGarageItemModal(scannedBarcode, fromScan) {
    if (!this.hasNewItemUrlValue || !window.$) {
      return
    }

    const url = new URL(this.newItemUrlValue, window.location.origin)
    url.searchParams.set("open_modal", "true")
    url.searchParams.set("garage_item[barcode]", scannedBarcode)
    if (fromScan) {
      this.inputTarget.value = ""
      url.searchParams.set("scanned_barcode", "true")
    }

    window.$.ajax({
      url: url.toString(),
      method: "GET",
      dataType: "script",
    })
  }

  showManualEntry(eventOrOnConnect = false) {
    const onConnect = eventOrOnConnect === true

    this.manualInputContainerTarget.style.display = "block"
    this.manualButtonTarget.style.display = "none"
    this.orTextTarget.style.display = "none"

    if (!onConnect) {
      this.inputTarget.focus()
    }

    this.hideItemFoundAlert()
  }

  hideItemFoundAlert() {}

  cancelManualInput() {
    this.manualInputContainerTarget.style.display = "none"
    this.manualButtonTarget.style.display = "inline-block"
    this.orTextTarget.style.display = "inline-block"

    this.showStatus("")
    this.inputTarget.value = ""
    this.toggleSubmitButtonDisabled()
  }

  removeCameraOption(on_connect = false) {
    if (this.cameraOptionRemovedValue !== true) {
      this.cameraOptionRemovedValue = true
    }
    this.scanOptionsContainerTarget.style.display = "none"
    this.cancelButtonTarget.style.display = "none"
    this.showManualEntry(on_connect)
  }

  markCameraUnavailable() {
    this.cameraOptionRemovedValue = true
    this.storeCameraUnavailable()
    this.removeCameraOption()
  }

  clearCameraUnavailable() {
    this.cameraOptionRemovedValue = false
    this.removeStoredCameraUnavailable()
  }

  cameraUnavailableStored() {
    try {
      return window.sessionStorage.getItem(CAMERA_UNAVAILABLE_STORAGE_KEY) === "true"
    } catch (_) {
      return false
    }
  }

  storeCameraUnavailable() {
    try {
      window.sessionStorage.setItem(CAMERA_UNAVAILABLE_STORAGE_KEY, "true")
    } catch (_) {}
  }

  removeStoredCameraUnavailable() {
    try {
      window.sessionStorage.removeItem(CAMERA_UNAVAILABLE_STORAGE_KEY)
    } catch (_) {}
  }

  shouldPersistCameraUnavailable(error) {
    if (!error || !error.name) {
      return false
    }

    return [
      "NotAllowedError",
      "PermissionDeniedError",
      "NotFoundError",
      "DevicesNotFoundError",
      "SecurityError",
    ].includes(error.name)
  }

  async cameraPermissionState() {
    if (!navigator.permissions) {
      return null
    }

    try {
      const permission = await navigator.permissions.query({ name: "camera" })
      return permission.state
    } catch (_) {
      return null
    }
  }

  async openCamera() {
    if (
      activeTyreBarcodeScannerController.current &&
      activeTyreBarcodeScannerController.current !== this
    ) {
      activeTyreBarcodeScannerController.current.stopCamera()
    }

    activeTyreBarcodeScannerController.current = this

    await this.primeScanSuccessAudioContext()

    this.inputTarget.value = ""
    this.toggleSubmitButtonDisabled()

    this.hideItemFoundAlert()
    this.manualButtonTarget.style.display = "none"
    this.manualInputContainerTarget.style.display = "none"
    this.orTextTarget.style.display = "none"
    this.startButtonTarget.style.display = "none"
    this.stopButtonTarget.style.display = "inline-block"
    this.showStatus(this.scanBarcodeStatusValue)
    this.cameraScanning = true

    this.fallbackToZxingInProgress = false

    const cameraPermissionState = await this.cameraPermissionState()
    if (cameraPermissionState === "denied") {
      this.stopCamera()
      this.markCameraUnavailable()
      this.showStatus(this.scanUnavailableStatusValue)
      return
    }

    if (this.barcodeDetector) {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.markCameraUnavailable()
        this.stopCamera()
        this.showStatus(this.scanUnavailableStatusValue)
        return
      }

      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
          },
          audio: false,
        })

        this.clearCameraUnavailable()

        this.videoTarget.srcObject = this.mediaStream
        await this.videoTarget.play()

        this.videoTrack = this.mediaStream.getVideoTracks()[0]
        this.videoTarget.style.display = "block"
        this.videoTarget.scrollIntoView({ behavior: "smooth", block: "center" })
        this.scanFromCamera()
      } catch (error) {
        this.stopCamera()
        if (this.shouldPersistCameraUnavailable(error)) {
          this.markCameraUnavailable()
        }
        this.showStatus(this.scanUnavailableStatusValue)
      }
      return
    }

    this.scanWithZxing()
  }

  stopCamera() {
    if (this.scanAnimationFrame) {
      window.cancelAnimationFrame(this.scanAnimationFrame)
      this.scanAnimationFrame = null
    }

    if (activeTyreBarcodeScannerController.current === this) {
      activeTyreBarcodeScannerController.current = null
    }

    this.cameraScanning = false
    this.resetPendingBarcode()
    this.stopZxing()

    if (this.videoTarget) {
      this.videoTarget.pause()
      this.videoTarget.srcObject = null
      this.videoTarget.style.display = "none"
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop())
      this.mediaStream = null
      this.videoTrack = null
    }

    this.startButtonTarget.style.display = "inline-block"
    this.stopButtonTarget.style.display = "none"
    this.manualButtonTarget.style.display = "inline-block"
    this.orTextTarget.style.display = "inline-block"

    this.showStatus("")
  }

  async scanFromCamera() {
    if (!this.cameraScanning || !this.videoTarget || !this.barcodeDetector || this.videoTarget.readyState < 2) {
      this.scanAnimationFrame = window.requestAnimationFrame(() => this.scanFromCamera())
      return
    }

    try {
      const barcodes = await this.barcodeDetector.detect(this.videoTarget)
      const barcodeValue = this.normalizedValue(barcodes[0] && barcodes[0].rawValue)

      if (barcodeValue && this.shouldAcceptScannedBarcode(barcodeValue, {
        requiredConfirmations: 3,
        useConfirmationWindow: true,
      })) {
        this.playScanSuccessBeep()
        this.inputTarget.value = barcodeValue
        this.stopCamera()
        this.submitBarcode()
        return
      }
    } catch (error) {
      if (!this.fallbackToZxingInProgress) {
        this.fallbackToZxingInProgress = true
        this.stopNativeCameraStream()
        this.barcodeDetector = null
        this.scanWithZxing()
        return
      }
    }

    this.scanAnimationFrame = window.requestAnimationFrame(() => this.scanFromCamera())
  }

  async scanWithZxing() {
    if (!this.hasVideoTarget) {
      return
    }

    try {
      this.showVideoForSafari()

      if (!this.videoTarget.id) {
        this.videoTarget.id = "scrut_tyre_barcode_scanner_video"
      }

      // Loaded on demand: @zxing/library is large (~400K) and only needed on the
      // native-BarcodeDetector fallback path, so esbuild code-splitting keeps it
      // out of the main app.js bundle until a scan actually reaches this point.
      const { BrowserMultiFormatReader } = await import("@zxing/library")
      this.zxingReader = new BrowserMultiFormatReader()

      await this.zxingReader.decodeFromConstraints(
        {
          video: {
            facingMode: { ideal: "environment" },
          },
        },
        this.videoTarget.id,
        (result) => {
          if (!this.cameraScanning || !result) {
            return
          }

          const barcodeValue = this.normalizedValue(result.getText && result.getText())
          if (!barcodeValue) {
            return
          }

          if(this.shouldAcceptScannedBarcode(barcodeValue, {
            requiredConfirmations: 3,
            useConfirmationWindow: false,
          })) {
            this.playScanSuccessBeep()
            this.inputTarget.value = barcodeValue
            this.stopCamera()
            this.submitBarcode()
          }
        }
      )
    } catch (error) {
      this.stopCamera()
      if (this.shouldPersistCameraUnavailable(error)) {
        this.markCameraUnavailable()
      }
      this.showStatus(this.scanUnavailableStatusValue)
    }
  }

  stopNativeCameraStream() {
    if (this.scanAnimationFrame) {
      window.cancelAnimationFrame(this.scanAnimationFrame)
      this.scanAnimationFrame = null
    }

    if (this.videoTarget) {
      this.videoTarget.pause()
      this.videoTarget.srcObject = null
      this.videoTarget.style.display = "none"
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop())
      this.mediaStream = null
      this.videoTrack = null
    }
  }

  stopZxing() {
    if (!this.hasVideoTarget) {
      return
    }

    this.videoTarget.style.display = "none"

    const zxingReader = this.zxingReader
    this.zxingReader = null

    if (!zxingReader) {
      return
    }

    Promise.resolve()
      .then(() => zxingReader.reset())
      .catch(() => {})
  }

  async primeScanSuccessAudioContext() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) {
      return
    }

    try {
      if (!this.scanBeepAudioContext || this.scanBeepAudioContext.state === "closed") {
        this.scanBeepAudioContext = new AudioContextClass()
      }

      if (this.scanBeepAudioContext.state === "suspended") {
        await this.scanBeepAudioContext.resume()
      }
    } catch (_) {
      // Do nothing if audio cannot be primed
    }
  }

  playScanSuccessBeep() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) {
      return
    }

    try {
      if (!this.scanBeepAudioContext || this.scanBeepAudioContext.state === "closed") {
        this.scanBeepAudioContext = new AudioContextClass()
      }

      const audioContext = this.scanBeepAudioContext
      const now = audioContext.currentTime

      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.type = "square"

      oscillator.frequency.setValueAtTime(1200, now)

      const duration = 0.22

      gainNode.gain.setValueAtTime(0.0001, now)
      gainNode.gain.exponentialRampToValueAtTime(0.35, now + 0.01)

      gainNode.gain.setValueAtTime(0.35, now + 0.10)

      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + duration)

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.start(now)
      oscillator.stop(now + duration)
    } catch (_) {
      // Do nothing if error so scan flow isn't interrupted
    }
  }

  buildBarcodeDetector() {
    if (typeof window.BarcodeDetector === "undefined") {
      return null
    }

    try {
      return new window.BarcodeDetector()
    } catch (_) {
      return null
    }
  }

  showStatus(message, item_selection = false) {
    if (!this.hasStatusTarget) {
      return
    }

    this.statusTarget.textContent = message
    this.statusTarget.style.display = message ? "block" : "none"
    if (item_selection) {
      this.statusTarget.classList.add("item_selection_status")
      clearTimeout(this.statusTimeout)
      this.statusTimeout = setTimeout(() => {
        this.statusTarget.style.display = "none"
      }, 3000)
    } else {
      this.statusTarget.classList.remove("item_selection_status")
    }
  }

  showVideoForSafari() {
    const video = this.videoTarget
    video.style.display = "block"

    const scroll = () => {
      const rect = video.getBoundingClientRect()

      const target =
        window.scrollY +
        rect.top -
        (window.innerHeight / 2) +
        (rect.height / 2)

      window.scrollTo({top: target, behavior: "smooth"})
    }

    const waitForStableLayout = () => {
      let lastHeight = 0
      let stableFrames = 0

      const check = () => {
        const h = video.getBoundingClientRect().height

        if (h === lastHeight && h > 0) {
          stableFrames++
        } else {
          stableFrames = 0
        }

        lastHeight = h

        if (stableFrames >= 2) {
          scroll()
        } else {
          requestAnimationFrame(check)
        }
      }

      requestAnimationFrame(check)
    }

    if (video.readyState >= 1) {
      waitForStableLayout()
    } else {
      video.addEventListener("loadedmetadata", waitForStableLayout, {once: true})
    }
  }
}
